import { type FC, useCallback, useMemo, useReducer, useState } from "react";

import {
  Alert,
  Box,
  Button,
  ChevronUpIcon,
  Column,
  EmptyState,
  Heading,
  HiddenFormLabel,
  IconBox,
  Row,
  SearchInput,
  Spinner,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { useQueryClient } from "react-query";

import { isExpiredCursor } from "src/components/lineage/lineage-pagination";
import { useRebuildPollLimit } from "src/components/lineage/use-rebuild-poll-limit";
import { getDisplayNameForResource } from "src/components/lineage/usage-panel/resource-display";
import { SkeletonItem } from "src/components/skeletons";
import {
  LineageStatus,
  type ResourceDependentsPageQuery,
  useResourceDependentsPageQuery,
  useResourceDependentsSummaryQuery,
} from "src/graphql";
import {
  type GovernedRefetchQuery,
  useGovernedRefetchInterval,
} from "src/hooks/use-governed-refetch-interval";
import {
  formatDatetimeWithTimezone,
  formatRelativeShort,
} from "src/utils/time";

import {
  loadResourceDependentsPage,
  RESOURCE_DEPENDENTS_PAGE_SIZE,
} from "./resource-dependents-page-loader";
import { UsageSummary } from "./usage-summary";
import { UsageTree } from "./usage-tree";
import {
  emptyTreeState,
  hasExpandableRows,
  hasExpandedRows,
  toResourceTypeCounts,
  usageTreeReducer,
  type UsageRow,
} from "./usage-tree-state";

const PAGE_LOAD_ERROR =
  "Something went wrong on our end. Try again, or contact support if it keeps happening.";

const QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

const USAGE_CACHE_TIME_MS = 5 * 60 * 1000; // 5 minutes

// The tree variant drops the summary, filters, and search
type UsagePanelVariant = "full" | "tree";

type UsagePanelProps = {
  resourceType: string;
  resourceId: string;
  variant?: UsagePanelVariant;
  name?: string;
  lastUpdatedAt?: string;
  // Fill the height of the container.
  fillHeight?: boolean;
  alwaysRefetch?: boolean;
};

// Stands in for the tree rows, which are a row of text at py={1.5}.
const UsageRowsSkeleton: FC = () => (
  <Column gap={1}>
    {[0, 1, 2, 3, 4].map((index) => (
      <SkeletonItem key={index} borderRadius="md" height={8} />
    ))}
  </Column>
);

type UsagePanelBodyProps = Pick<
  UsagePanelProps,
  "resourceType" | "resourceId" | "variant" | "fillHeight" | "alwaysRefetch"
>;

type UsagePanelHeaderProps = {
  resourceType: string;
  name: string;
  lastUpdatedAt?: string;
};

/**
 * Used to display all the downstream resources for a given resource.
 * This panel fetches data using the lineage graph API.
 */
export const UsagePanel: FC<UsagePanelProps> = ({
  resourceType,
  resourceId,
  variant = "full",
  name,
  lastUpdatedAt,
  fillHeight,
  alwaysRefetch,
}) => {
  return (
    <Column flex={fillHeight ? 1 : undefined} minHeight={0}>
      {name && (
        <UsagePanelHeader
          resourceType={resourceType}
          name={name}
          lastUpdatedAt={lastUpdatedAt}
        />
      )}
      <UsagePanelBody
        key={`${resourceType}:${resourceId}`}
        resourceType={resourceType}
        resourceId={resourceId}
        variant={variant}
        fillHeight={fillHeight}
        alwaysRefetch={alwaysRefetch}
      />
    </Column>
  );
};

const UsagePanelBody: FC<UsagePanelBodyProps> = ({
  resourceType,
  resourceId,
  variant,
  fillHeight,
  alwaysRefetch,
}) => {
  const isTreeOnly = variant === "tree";
  const freshness = alwaysRefetch
    ? { cacheTime: 0, refetchOnMount: "always" as const }
    : { cacheTime: USAGE_CACHE_TIME_MS, refetchOnMount: false as const };
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(
    usageTreeReducer,
    undefined,
    emptyTreeState,
  );
  const [selectedTypes, setSelectedTypes] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [search, setSearch] = useState("");
  const [isReloading, setIsReloading] = useState(false);

  // Refetch the page when the status changes or the partitions change.
  const { refetchInterval: getGovernedRefetchInterval } =
    useGovernedRefetchInterval<ResourceDependentsPageQuery>({
      baseMs: 5000,
      compareKey: (data) => [
        data?.resourceDependentsPage.status,
        ...(data?.resourceDependentsPage.partitions ?? []).map(
          (partition) =>
            `${partition.ownerResourceType}:${partition.status}:${partition.reason}`,
        ),
      ],
    });

  // While the graph rebuilds, poll it. The interval stretches on its own as the
  // graph stops changing, and the poll stops for good once the rebuild has taken
  // more attempts than a rebuild should need.
  const rebuildPoll = useRebuildPollLimit();
  const getRebuildingRefetchInterval = useCallback(
    (
      data: ResourceDependentsPageQuery | undefined,
      query?: GovernedRefetchQuery,
    ) =>
      data?.resourceDependentsPage.status === LineageStatus.Rebuilding &&
      !rebuildPoll.hasGivenUp
        ? getGovernedRefetchInterval(data, query)
        : false,
    [getGovernedRefetchInterval, rebuildPoll.hasGivenUp],
  );

  const firstPage = useResourceDependentsPageQuery(
    {
      resourceType,
      resourceId,
      limit: RESOURCE_DEPENDENTS_PAGE_SIZE,
    },
    {
      ...QUERY_OPTIONS,
      ...freshness,
      keepPreviousData: false,
      refetchInterval: getRebuildingRefetchInterval,
      onSuccess: (data) =>
        rebuildPoll.observe(data.resourceDependentsPage.status),
      // react-query keeps the last successful data on error, so without this a
      // failing read would leave a rebuilding status on screen and the poll
      // armed against it forever.
      onError: () => rebuildPoll.observe(undefined),
    },
  );

  const page = firstPage.data?.resourceDependentsPage;

  // Set the tree state when the first page is loaded.
  if (
    page?.status === LineageStatus.Ready &&
    page.snapshotToken &&
    page.snapshotToken !== state.snapshotToken
  ) {
    dispatch({
      type: "snapshot-loaded",
      snapshotToken: page.snapshotToken,
      page,
    });
  }

  // Every later read should use the same snapshot token.
  const { snapshotToken, hasExpired } = state;

  // Summary is used to display the total number of downstream resources
  // at the top of the panel.
  const summary = useResourceDependentsSummaryQuery(
    {
      resourceType,
      resourceId,
      snapshotToken: snapshotToken ?? "",
    },
    {
      ...QUERY_OPTIONS,
      ...freshness,
      enabled: !isTreeOnly && Boolean(snapshotToken) && !hasExpired,
      keepPreviousData: false,
      onError: (error) => {
        if (snapshotToken && isExpiredCursor(error)) {
          dispatch({ type: "snapshot-expired", snapshotToken });
        }
      },
    },
  );

  // Used to load the root page.
  const loadRootPage = useCallback(
    async (cursor: string) => {
      if (!snapshotToken) {
        return;
      }

      dispatch({ type: "root-page-requested", cursor });

      try {
        const result = await loadResourceDependentsPage({
          queryClient,
          resourceType,
          resourceId,
          cursor,
          snapshotToken,
        });
        dispatch({
          type: "root-page-loaded",
          snapshotToken,
          cursor,
          page: result.resourceDependentsPage,
        });
      } catch (error) {
        dispatch(
          isExpiredCursor(error)
            ? { type: "snapshot-expired", snapshotToken }
            : { type: "root-page-failed", snapshotToken, cursor },
        );
      }
    },
    [queryClient, resourceId, resourceType, snapshotToken],
  );

  // Used to load the children of a row.
  const loadChildren = useCallback(
    async (row: UsageRow) => {
      if (!snapshotToken) {
        return;
      }

      const cursor = row.childrenLoaded ? row.childCursor : null;
      dispatch({ type: "children-requested", key: row.key, cursor });

      try {
        const result = await loadResourceDependentsPage({
          queryClient,
          resourceType: row.node.resourceType,
          resourceId: row.node.resourceId,
          cursor,
          snapshotToken,
        });
        dispatch({
          type: "children-loaded",
          snapshotToken,
          key: row.key,
          cursor,
          page: result.resourceDependentsPage,
        });
      } catch (error) {
        dispatch(
          isExpiredCursor(error)
            ? { type: "snapshot-expired", snapshotToken }
            : { type: "children-failed", snapshotToken, key: row.key, cursor },
        );
      }
    },
    [queryClient, snapshotToken],
  );

  // Used to toggle the expansion of a row.
  const onToggle = useCallback(
    (row: UsageRow) => {
      dispatch({ type: "row-toggled", key: row.key });
      if (
        !row.childrenLoaded &&
        !row.isLoadingChildren &&
        row.directDependentCount > 0
      ) {
        void loadChildren(row);
      }
    },
    [loadChildren],
  );

  // Used to filter the resources by type.
  const onToggleType = (resource: string) => {
    setSelectedTypes((current) => {
      const next = new Set(current);
      if (next.has(resource)) {
        next.delete(resource);
      } else {
        next.add(resource);
      }
      return next;
    });
  };

  // In the case of an error, reload the page to try again.
  const reload = async () => {
    if (isReloading) {
      return;
    }
    setIsReloading(true);
    dispatch({ type: "reload-requested" });
    rebuildPoll.reset();
    try {
      await firstPage.refetch();
    } finally {
      setIsReloading(false);
    }
  };

  // A reader who asks to keep waiting gets the full count of attempts again.
  const checkAgain = () => {
    rebuildPoll.reset();
    void firstPage.refetch();
  };

  // This determines when the graph was last generated. It's pulled from the first page.
  const lastCalculatedAt = useMemo(() => {
    const completedAt = (page?.partitions ?? [])
      .map((partition) => partition.snapshotCompletedAt)
      .filter((value): value is string => Boolean(value))
      .map((value) => new Date(value).getTime());
    return completedAt.length > 0 ? new Date(Math.max(...completedAt)) : null;
  }, [page]);

  const summaryStats = summary.data?.resourceDependentsSummary;
  // Zero is a count the summary shows, so the guard is for a count the read
  // could not produce rather than for a falsy one.
  const summaryCounts =
    summaryStats?.directDependents != null &&
    summaryStats?.indirectDependents != null
      ? {
          directDependents: summaryStats.directDependents,
          indirectDependents: summaryStats.indirectDependents,
        }
      : null;
  const countsByType = useMemo(
    () => toResourceTypeCounts(summaryStats?.dependentCountsByType ?? []),
    [summaryStats?.dependentCountsByType],
  );

  // If a filter is removed on load, show all types as a fallback.
  const activeTypes = useMemo(
    () =>
      countsByType.size === 0
        ? selectedTypes
        : new Set([...selectedTypes].filter((type) => countsByType.has(type))),
    [countsByType, selectedTypes],
  );

  const hasTree = state.orderedKeys.length > 0;

  if (firstPage.isLoading) {
    return (
      <Column gap={3} p={4}>
        <SkeletonItem borderRadius="md" height={20} />
        <SkeletonItem borderRadius="md" height={10} />
        <UsageRowsSkeleton />
      </Column>
    );
  }

  // If the tree is not loaded, show an error message.
  if (!hasTree) {
    if (firstPage.error) {
      return (
        <EmptyState
          m={4}
          title="Couldn’t load usage"
          message={PAGE_LOAD_ERROR}
        />
      );
    }

    if (page?.status === LineageStatus.Rebuilding) {
      if (rebuildPoll.hasGivenUp) {
        return (
          <EmptyState
            m={4}
            title="Still calculating usage"
            message="This is taking longer than usual. Check again in a few minutes, or contact support if it keeps happening."
            actions={<Button onClick={checkAgain}>Check again</Button>}
          />
        );
      }

      return (
        <Column gap={3} p={4} role="status" aria-live="polite">
          <Row align="center" gap={2}>
            <Spinner size="sm" />
            <Text size="sm" color="text.secondary">
              Calculating usage
            </Text>
          </Row>
          <UsageRowsSkeleton />
        </Column>
      );
    }

    if (!page?.rows) {
      return (
        <EmptyState
          m={4}
          title="Couldn’t calculate usage"
          message={PAGE_LOAD_ERROR}
        />
      );
    }

    if (page.rows.length === 0) {
      return (
        <EmptyState
          variant="minimal"
          mx={4}
          my={10}
          title="Nothing uses this resource"
          message="No other resource in your workspace references this one."
        />
      );
    }
  }

  return (
    <Column gap={3} p={4} flex={fillHeight ? 1 : undefined} minHeight={0}>
      {!isTreeOnly &&
        (summaryCounts ? (
          <UsageSummary
            directDependents={summaryCounts.directDependents}
            indirectDependents={summaryCounts.indirectDependents}
            countsByType={countsByType}
            selectedTypes={activeTypes}
            onToggleType={onToggleType}
            lastCalculatedAt={lastCalculatedAt}
          />
        ) : summary.error && !isExpiredCursor(summary.error) ? (
          <Alert
            variant="inline"
            type="warning"
            title="Couldn’t load the usage summary"
            message={PAGE_LOAD_ERROR}
            actions={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void summary.refetch()}
              >
                Retry
              </Button>
            }
          />
        ) : (
          <SkeletonItem borderRadius="md" height={20} />
        ))}

      {!isTreeOnly && (
        <HiddenFormLabel label="Search loaded resources">
          <SearchInput
            width="100%"
            placeholder="Search loaded resources"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </HiddenFormLabel>
      )}

      {page?.status === LineageStatus.Rebuilding &&
        (rebuildPoll.hasGivenUp ? (
          <Alert
            variant="inline"
            type="warning"
            title="Still calculating usage"
            message="This is taking longer than usual, so we stopped checking. These results are from the last completed run."
            actions={
              <Button size="sm" variant="secondary" onClick={checkAgain}>
                Check again
              </Button>
            }
          />
        ) : (
          <Row align="center" gap={2} role="status" aria-live="polite">
            <Spinner size="sm" />
            <Text size="sm" color="text.secondary">
              Recalculating usage. These results are from the last completed
              run.
            </Text>
          </Row>
        ))}

      {firstPage.error && (
        <Alert
          variant="inline"
          type="warning"
          title="Couldn’t check for newer results"
          message={PAGE_LOAD_ERROR}
        />
      )}

      {hasExpired && (
        <Alert
          variant="inline"
          type="warning"
          title="Usage changed while this page was open"
          message="These results are from an earlier calculation, so you can’t load more rows. Reload to see the latest usage."
          actions={
            <Button
              size="sm"
              isLoading={isReloading}
              onClick={() => void reload()}
            >
              Reload
            </Button>
          }
        />
      )}

      <Row align="center" justify="space-between">
        <Text size="sm" color="text.secondary" fontWeight="medium">
          Used by
        </Text>
        {hasExpandableRows(state) && (
          <Button
            icon={ChevronUpIcon}
            size="sm"
            variant="secondary"
            isDisabled={!hasExpandedRows(state)}
            onClick={() => dispatch({ type: "collapsed-all" })}
          >
            Collapse all
          </Button>
        )}
      </Row>

      <UsageTree
        fillHeight={fillHeight}
        state={state}
        filter={{ types: activeTypes, search }}
        onToggle={onToggle}
        onLoadMoreChildren={loadChildren}
        onLoadMorePage={loadRootPage}
        isLoadingDisabled={hasExpired}
        emptyState={
          <EmptyState
            m={4}
            title="No matching resources"
            message="No loaded resource matches these filters. Only loaded resources are searched, so try clearing a filter or loading more."
          />
        }
      />
    </Column>
  );
};

const UsagePanelHeader: FC<UsagePanelHeaderProps> = ({
  resourceType,
  name,
  lastUpdatedAt,
}) => {
  const display = getDisplayNameForResource(resourceType);
  const Icon = display.icon;
  const relativeUpdatedAt = lastUpdatedAt
    ? formatRelativeShort(lastUpdatedAt)
    : "";
  const absoluteUpdatedAt = lastUpdatedAt
    ? formatDatetimeWithTimezone(lastUpdatedAt)
    : null;

  return (
    <Column
      gap={3}
      p={4}
      borderBottomWidth="hairline"
      borderBottomColor="base.divider"
      flexShrink={0}
    >
      <Row align="center" gap={2} minWidth={0}>
        <IconBox
          size="md"
          color={display.color}
          icon={<Icon />}
          aria-hidden
          display="flex"
        />
        <Tooltip message={name}>
          <Heading size="md" isTruncated>
            {name}
          </Heading>
        </Tooltip>
      </Row>

      <Row align="center" gap={2}>
        <Text color="text.secondary">{display.label}</Text>
        {relativeUpdatedAt && (
          <>
            <Box
              height={5}
              borderLeftWidth="hairline"
              borderLeftColor="base.divider"
              aria-hidden
            />
            <Tooltip message={absoluteUpdatedAt ?? lastUpdatedAt}>
              <Text color="text.secondary">Updated {relativeUpdatedAt}</Text>
            </Tooltip>
          </>
        )}
      </Row>
    </Column>
  );
};
